// Dashboard complet de gestion des interventions
// Fichier: /var/www/squarecom/frontend/src/pages/InterventionDashboard.js

import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Paper, Typography, Box, Card, CardContent, Button, IconButton,
  Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, Divider, Switch, FormControlLabel,
  Badge, LinearProgress, Stack
} from '@mui/material';
import {
  Dashboard, Build, LocationOn, Schedule, CheckCircle, Warning, Assignment,
  CalendarToday, Refresh
} from '@mui/icons-material';

function InterventionDashboard() {
  const [interventions, setInterventions] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedIntervention, setSelectedIntervention] = useState(null);

  const [assignDialog, setAssignDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0, pending: 0, assigned: 0, inProgress: 0, completed: 0, urgent: 0
  });

  const [assignmentForm, setAssignmentForm] = useState({
    technicianId: '', scheduledDate: '', scheduledTime: '',
    estimatedDuration: 2, estimatedCost: 0, instructions: '',
    sendNotification: true, requirePhotoBefore: true, requirePhotoAfter: true
  });

  const [closeForm, setCloseForm] = useState({
    resolutionNote: '',
    actualDuration: 1,
    actualCost: 0,
    notifyRequester: true,
  });

  // Date min = aujourd'hui
  const todayStr = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const intResponse = await fetch('/api/interventions/all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const intData = await intResponse.json();
      const list = intData.interventions || [];
      setInterventions(list);

      setStats({
        total: list.length,
        pending: list.filter(i => i.status === 'pending').length,
        assigned: list.filter(i => i.status === 'assigned').length,
        inProgress: list.filter(i => i.status === 'in_progress').length,
        completed: list.filter(i => i.status === 'completed').length,
        urgent: list.filter(i => i.priority === 'urgent').length
      });
    } catch (e) {
      console.error('Erreur chargement:', e);
    } finally {
      setLoading(false);
    }
  };

  // Liste des techniciens
  const loadAvailableTechnicians = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/technicians', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setTechnicians(Array.isArray(data.technicians) ? data.technicians : []);
    } catch (e) {
      console.error('Erreur chargement techniciens:', e);
      setTechnicians([]);
    }
  };

  const handleAssignClick = (intervention) => {
    setSelectedIntervention(intervention);
    loadAvailableTechnicians();
    setAssignDialog(true);
  };

  const handleAssignSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const scheduledDateTime = assignmentForm.scheduledDate && assignmentForm.scheduledTime
        ? `${assignmentForm.scheduledDate}T${assignmentForm.scheduledTime}:00` : null;

      const resp = await fetch(`/api/interventions/${selectedIntervention.id}/assign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          technicianId: assignmentForm.technicianId,
          scheduledDate: scheduledDateTime,
          estimatedDuration: assignmentForm.estimatedDuration,
          estimatedCost: assignmentForm.estimatedCost,
          instructions: assignmentForm.instructions,
          sendNotification: assignmentForm.sendNotification,
          requirePhotoBefore: assignmentForm.requirePhotoBefore,
          requirePhotoAfter: assignmentForm.requirePhotoAfter
        })
      });
      if (!resp.ok) throw new Error('Assignation échouée');

      setAssignDialog(false);
      setAssignmentForm({
        technicianId: '', scheduledDate: '', scheduledTime: '',
        estimatedDuration: 2, estimatedCost: 0, instructions: '',
        sendNotification: true, requirePhotoBefore: true, requirePhotoAfter: true
      });
      loadDashboardData();
    } catch (e) {
      console.error('Erreur attribution:', e);
    }
  };

  // Ouvrir la clôture
  const handleCloseClick = (intervention) => {
    setSelectedIntervention(intervention);
    setCloseForm({ resolutionNote: '', actualDuration: 1, actualCost: 0, notifyRequester: true });
    setCloseDialog(true);
  };

  const handleCloseSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`/api/interventions/${selectedIntervention.id}/close`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(closeForm)
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(e.error || 'Clôture échouée');
      }
      setCloseDialog(false);
      loadDashboardData();
    } catch (e) {
      console.error('Erreur clôture:', e);
    }
  };

  // Helpers UI
  const getPriorityColor = (priority) => ({ urgent: 'error', high: 'warning', medium: 'info', low: 'success' }[priority] || 'default');
  const getStatusIcon = (status) => ({ pending: <Schedule />, assigned: <Assignment />, in_progress: <Build />, completed: <CheckCircle /> }[status] || <Schedule />);
  const getCategoryIcon = (category) => {
    if (!category) return '🔧';
    const icons = { 'Plomberie':'🚰','Électricité':'💡','Chauffage':'🔥','Ascenseur':'🛗','Serrurerie':'🔐','Nettoyage':'🧹','Jardinage':'🌳','Peinture':'🎨','Éclairage':'💡' };
    return icons[category] || '🔧';
  };
  const getInterventionAge = (createdAt) => {
    const hours = (Date.now() - new Date(createdAt)) / 36e5;
    if (hours < 1) return 'Nouveau';
    if (hours < 24) return `${Math.floor(hours)}h`;
    return `${Math.floor(hours / 24)}j`;
  };
  const getResidenceName = (i) => i?.residenceName || i?.residence?.name || i?.Residence?.name || i?.residence || i?.location || '';

  const pendingInterventions   = interventions.filter(i => i.status === 'pending');
  const assignedInterventions  = interventions.filter(i => i.status === 'assigned');
  const inProgressInterventions= interventions.filter(i => i.status === 'in_progress');

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4"><Dashboard sx={{ mr: 1, verticalAlign: 'middle' }} />Centre de Gestion des Interventions</Typography>
        <IconButton onClick={loadDashboardData}><Refresh /></IconButton>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          ['Total', stats.total, undefined],
          ['En attente', stats.pending, 'warning.light'],
          ['Assignées', stats.assigned, 'info.light'],
          ['En cours', stats.inProgress, 'primary.light'],
          ['Terminées', stats.completed, 'success.light'],
        ].map(([label, value, bg], idx) => (
          <Grid key={idx} item xs={12} sm={6} md={2}>
            <Card sx={bg ? { bgcolor: bg } : {}}>
              <CardContent>
                <Typography color={bg ? 'white' : 'textSecondary'} gutterBottom variant="body2">{label}</Typography>
                <Typography variant="h4" color={bg ? 'white' : undefined}>{value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: stats.urgent > 0 ? 'error.light' : 'grey.300' }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">Urgentes</Typography>
              <Typography variant="h4" color="white">{stats.urgent}</Typography>
              {stats.urgent > 0 && <Warning sx={{ color: 'white' }} />}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Kanban */}
      <Grid container spacing={2}>
        {/* En attente */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom><Badge badgeContent={pendingInterventions.length} color="warning">En attente</Badge></Typography>
            <Divider sx={{ mb: 2 }} />
            {pendingInterventions.map(intervention => (
              <Card key={intervention.id} sx={{ mb: 2, borderLeft: `4px solid ${intervention.priority === 'urgent' ? 'red' : 'orange'}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">{getCategoryIcon(intervention.category)} {intervention.title}</Typography>
                    <Chip label={getInterventionAge(intervention.createdAt)} size="small" color={intervention.priority === 'urgent' ? 'error' : 'default'} />
                  </Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <LocationOn fontSize="small" /> {getResidenceName(intervention)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{intervention.description?.substring(0, 100)}...</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip label={intervention.priority} size="small" color={getPriorityColor(intervention.priority)} />
                    <Button size="small" variant="contained" onClick={() => handleAssignClick(intervention)}>Attribuer</Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Paper>
        </Grid>

        {/* Assignées */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom><Badge badgeContent={assignedInterventions.length} color="info">Assignées</Badge></Typography>
            <Divider sx={{ mb: 2 }} />
            {assignedInterventions.map(intervention => (
              <Card key={intervention.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{getCategoryIcon(intervention.category)} {intervention.title}</Typography>
                  <Typography variant="body2" color="textSecondary"><LocationOn fontSize="small" /> {getResidenceName(intervention)}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => handleAssignClick(intervention)}>Réassigner</Button>
                    <Button size="small" variant="contained" color="success" onClick={() => handleCloseClick(intervention)}>Clore</Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Paper>
        </Grid>

        {/* En cours */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom><Badge badgeContent={inProgressInterventions.length} color="primary">En cours</Badge></Typography>
            <Divider sx={{ mb: 2 }} />
            {inProgressInterventions.map(intervention => (
              <Card key={intervention.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{getCategoryIcon(intervention.category)} {intervention.title}</Typography>
                  <Typography variant="body2" color="textSecondary"><LocationOn fontSize="small" /> {getResidenceName(intervention)}</Typography>
                  <LinearProgress sx={{ mt: 2, mb: 1 }} />
                  <Button size="small" variant="contained" color="success" onClick={() => handleCloseClick(intervention)}>Clore</Button>
                </CardContent>
              </Card>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog attribution */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Attribuer l'intervention
          {selectedIntervention && (
            <>
              <Typography variant="subtitle2" color="textSecondary">{selectedIntervention.title}</Typography>
              <Typography variant="subtitle2" color="textSecondary"><LocationOn fontSize="small" style={{ verticalAlign: 'middle' }} /> {getResidenceName(selectedIntervention)}</Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="tech-select-label">Sélectionner un technicien</InputLabel>
                <Select
                  labelId="tech-select-label" label="Sélectionner un technicien"
                  value={assignmentForm.technicianId}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, technicianId: e.target.value })}
                >
                  {technicians.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.firstName} {t.lastName} — {t.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth type="date" label="Date d'intervention"
                value={assignmentForm.scheduledDate}
                onChange={(e) => {
                  const v = e.target.value;
                  const safe = v && v < todayStr ? todayStr : v;
                  setAssignmentForm({ ...assignmentForm, scheduledDate: safe });
                }}
                InputLabelProps={{ shrink: true }} inputProps={{ min: todayStr }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth type="time" label="Heure"
                value={assignmentForm.scheduledTime}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, scheduledTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Durée estimée (heures)"
                value={assignmentForm.estimatedDuration}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, estimatedDuration: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Coût estimé (€)"
                value={assignmentForm.estimatedCost}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, estimatedCost: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Instructions pour le technicien"
                value={assignmentForm.instructions}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, instructions: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={assignmentForm.sendNotification}
                                 onChange={(e) => setAssignmentForm({ ...assignmentForm, sendNotification: e.target.checked })} />}
                label="Envoyer une notification au technicien"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleAssignSubmit}
                  disabled={!assignmentForm.technicianId || !assignmentForm.scheduledDate}>
            Attribuer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog clôture */}
      <Dialog open={closeDialog} onClose={() => setCloseDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Clôturer l'intervention
          {selectedIntervention && (
            <>
              <Typography variant="subtitle2" color="textSecondary">{selectedIntervention.title}</Typography>
              <Typography variant="subtitle2" color="textSecondary"><LocationOn fontSize="small" style={{ verticalAlign: 'middle' }} /> {getResidenceName(selectedIntervention)}</Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={4} label="Résolution / travaux effectués"
                value={closeForm.resolutionNote}
                onChange={(e) => setCloseForm({ ...closeForm, resolutionNote: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth type="number" label="Durée réelle (heures)"
                value={closeForm.actualDuration}
                onChange={(e) => setCloseForm({ ...closeForm, actualDuration: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth type="number" label="Coût réel (€)"
                value={closeForm.actualCost}
                onChange={(e) => setCloseForm({ ...closeForm, actualCost: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={closeForm.notifyRequester}
                                 onChange={(e) => setCloseForm({ ...closeForm, notifyRequester: e.target.checked })} />}
                label="Notifier le demandeur"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialog(false)}>Annuler</Button>
          <Button variant="contained" color="success"
                  onClick={handleCloseSubmit}
                  disabled={!closeForm.resolutionNote}>
            Clôturer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default InterventionDashboard;
